import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { industryService, Industry } from '../services/industryService';
import { absoluteUrl } from '../lib/seo';

const ActivityDetail = () => {
  const { industryId } = useParams();
  const [industry, setIndustry] = useState<Industry | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = industryService.subscribe((industries) => {
      const foundIndustry = industries.find((ind) => ind.id === industryId);
      setIndustry(foundIndustry || null);
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [industryId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-orange" />
      </div>
    );
  }

  if (!industry) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold mb-4 text-center">Secteur non trouve</h1>
        <p className="text-gray-600 mb-8 text-center">
          Le secteur d activite que vous recherchez n existe pas.
        </p>
        <Link
          to="/activites"
          className="bg-neon-orange text-white px-6 py-3 rounded-full font-semibold hover:bg-[#FF4500] transition-colors duration-300"
        >
          Voir tous les secteurs
        </Link>
      </div>
    );
  }

  const canonicalUrl = absoluteUrl(`/activites/${industry.id}`);
  const industryFaqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: industry.faq.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const industryServiceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Solutions digitales pour ${industry.name}`,
    description: industry.description,
    serviceType: industry.name,
    provider: {
      '@type': 'Organization',
      name: 'Couteau Suisse Digital',
      url: absoluteUrl('/'),
    },
    areaServed: 'CA',
    url: canonicalUrl,
  };

  const deliveryFramework = [
    {
      title: 'Positionnement et parcours',
      description: `Nous alignons le discours, la preuve, le design et la structure de conversion avec les attentes du secteur ${industry.name}.`
    },
    {
      title: 'Operations et automatisation',
      description: 'Nous simplifions les flux internes, les validations, le suivi commercial et les actions repetitives pour gagner en vitesse.'
    },
    {
      title: 'Pilotage et croissance',
      description: 'Nous relions la data, le reporting et les signaux business pour permettre de meilleures decisions et une execution plus solide.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{industry.metaTitle}</title>
        <meta name="description" content={industry.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={industry.metaTitle} />
        <meta property="og:description" content={industry.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(industryServiceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(industryFaqSchema)}</script>
      </Helmet>

      <main>
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,106,0,0.22),_transparent_38%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <Link
              to="/activites"
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-8 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Retour aux secteurs
            </Link>

            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="flex items-center gap-4"
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-white/10 text-4xl backdrop-blur">
                    {industry.icon}
                  </div>
                  <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-200 backdrop-blur">
                    Solutions pour {industry.name}
                  </div>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.08 }}
                  className="mt-6 max-w-5xl text-4xl font-bold leading-tight text-white md:text-6xl"
                >
                  Une execution digitale
                  <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> ajustee aux realites du secteur</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.16 }}
                  className="mt-6 max-w-3xl text-lg leading-8 text-gray-300 md:text-xl"
                >
                  {industry.description}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.24 }}
                  className="mt-8 grid gap-4 sm:grid-cols-3"
                >
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                    <div className="text-3xl font-bold text-neon-orange">{industry.stats.clients}+</div>
                    <div className="mt-2 text-sm text-gray-300">clients accompagnes</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                    <div className="text-3xl font-bold text-neon-orange">{industry.stats.projectsCompleted}+</div>
                    <div className="mt-2 text-sm text-gray-300">projets menes</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                    <div className="text-3xl font-bold text-neon-orange">{industry.stats.roi}</div>
                    <div className="mt-2 text-sm text-gray-300">ROI moyen observe</div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.24 }}
                className="rounded-[30px] border border-white/10 bg-white/5 p-6 text-white backdrop-blur"
              >
                <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Synthese executive</p>
                <div className="mt-5 space-y-4">
                  {industry.challenges.slice(0, 3).map((challenge) => (
                    <div key={challenge} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                      <p className="text-sm leading-6 text-gray-200">{challenge}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 rounded-2xl border border-white/10 bg-neon-orange/10 p-5">
                  <p className="text-sm leading-6 text-gray-100">
                    Nous combinons positionnement, architecture, automatisation et pilotage pour construire une
                    solution plus credible, plus simple a exploiter et plus rentable.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-18 bg-gradient-to-b from-[#f8f6f2] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {industry.benefits.map((benefit, index) => (
                <motion.div
                  key={benefit}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.06 }}
                  viewport={{ once: true }}
                  className="rounded-[26px] border border-gray-200 bg-white p-6 shadow-[0_18px_60px_rgba(17,24,39,0.05)]"
                >
                  <div className="text-sm uppercase tracking-[0.18em] text-neon-orange">Impact</div>
                  <p className="mt-4 text-lg font-semibold leading-8 text-gray-900">{benefit}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="rounded-[30px] bg-gray-950 p-7 text-white"
              >
                <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Lecture du secteur</p>
                <h2 className="mt-4 text-3xl font-bold">Pourquoi ce secteur demande une reponse specifique</h2>
                <div className="mt-6 text-base leading-8 text-gray-300 whitespace-pre-line">
                  {industry.longDescription}
                </div>
              </motion.div>

              <div className="grid gap-4">
                {deliveryFramework.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="rounded-[26px] border border-gray-200 bg-[#fbfaf8] p-6"
                  >
                    <div className="text-sm uppercase tracking-[0.18em] text-neon-orange">Etape {index + 1}</div>
                    <h3 className="mt-3 text-2xl font-semibold text-gray-900">{item.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-gray-600">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-gradient-to-b from-[#f8f6f2] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Notre expertise</p>
              <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Les leviers que nous activons</h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {industry.expertise.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.06 }}
                  viewport={{ once: true }}
                  className="rounded-[26px] border border-gray-200 bg-white p-5 shadow-[0_14px_40px_rgba(17,24,39,0.04)]"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1 h-2.5 w-2.5 rounded-full bg-neon-orange" />
                    <p className="text-base leading-7 text-gray-700">{item}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Solutions</p>
              <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Les systemes que nous construisons</h2>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              {industry.solutions.map((solution, index) => (
                <motion.div
                  key={solution.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="rounded-[30px] border border-gray-200 bg-white p-7 shadow-[0_22px_70px_rgba(17,24,39,0.06)]"
                >
                  <div className="inline-flex rounded-full border border-neon-orange/20 bg-neon-orange/10 px-3 py-1 text-xs uppercase tracking-[0.18em] text-neon-orange">
                    Solution {index + 1}
                  </div>
                  <h3 className="mt-5 text-2xl font-bold text-gray-900">{solution.name}</h3>
                  <p className="mt-4 text-base leading-7 text-gray-600">{solution.description}</p>

                  <div className="mt-6 rounded-2xl bg-[#f8f6f2] p-5">
                    <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Benefices</p>
                    <div className="mt-4 space-y-3">
                      {solution.benefits.map((benefit) => (
                        <div key={benefit} className="flex items-start gap-3">
                          <div className="mt-1 h-2.5 w-2.5 rounded-full bg-neon-orange" />
                          <p className="text-sm leading-6 text-gray-600">{benefit}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {solution.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-gradient-to-b from-[#f8f6f2] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Temoignages</p>
              <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Ce que disent nos clients</h2>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              {industry.testimonials.map((testimonial, index) => (
                <motion.div
                  key={`${testimonial.author}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="rounded-[30px] border border-gray-200 bg-white p-7 shadow-[0_22px_70px_rgba(17,24,39,0.06)]"
                >
                  <div className="text-neon-orange">Temoignage</div>
                  <p className="mt-5 text-base leading-8 text-gray-700">{testimonial.content}</p>
                  <div className="mt-6 border-t border-gray-100 pt-5">
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="mt-1 text-sm text-gray-500">{testimonial.position}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Questions frequentes</h2>
            <div className="space-y-5">
              {industry.faq.map((item, index) => (
                <motion.div
                  key={`${item.question}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.06 }}
                  viewport={{ once: true }}
                  className="rounded-[24px] border border-gray-200 bg-[#fbfaf8] p-6"
                >
                  <h3 className="text-xl font-semibold text-gray-900">{item.question}</h3>
                  <p className="mt-4 text-base leading-7 text-gray-600">{item.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,106,0,0.16),_transparent_40%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-white md:text-5xl"
            >
              Pret a transformer votre activite dans ce secteur ?
            </motion.h2>

            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-300">
              Nous pouvons cadrer la meilleure combinaison entre design, technologie, IA et operations pour votre
              contexte metier.
            </p>

            <Link
              to="/contact"
              className="mt-10 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-neon-orange to-[#FF4500] px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-neon-orange/20"
            >
              Planifier un appel strategique
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default ActivityDetail;
